import Blink from '@components/Blink'
import useHeaderSearchBar from '@hooks/useHeaderSearchBar'
import useTracking from '@hooks/useTracking'
import { getLanguage } from '@i18n'
import { desktopCSS, tabletCSS } from '@measures/responsive'
import { setCookieValue } from '@utils/cookie'
import { FunctionComponent, useCallback } from 'react'
import styled, { css } from 'styled-components'

type Locale = 'de' | 'fr'

interface StyledClickableProps {
  selected: boolean
}

interface ContainerProps {
  isLanguageSwitcherVisible: boolean
}

const Container = styled.div<ContainerProps>`
  ${({ isLanguageSwitcherVisible }) => css`
    height: 100%;
    display: flex;
    flex-direction: row;
    align-items: center;
    align-self: center;
    overflow: hidden;
    // width 106px consists of: 48px buttons width + divider and 4px for margins
    width: 106px;

    transition: width 0.2s ease-in-out;

    ${isLanguageSwitcherVisible &&
    css`
      ${desktopCSS(
        css`
          width: 0;
        `
      )}

      ${tabletCSS(
        css`
          width: 0;
        `
      )}
    `}
  `}
`

const StyledClickable = styled(Blink)<StyledClickableProps>`
  ${({
    theme: {
      colors: {
        brandColors: { brand_55 },
        grey: { grey_70 },
      },
      fonts: { goodOT },
    },
    selected,
  }) => {
    return css`
      color: ${selected ? brand_55 : grey_70};
      font-family: ${goodOT};
      font-size: 15px;
      font-weight: 500;
      text-decoration: none;
      width: 48px;
      height: 48px;
      display: grid;
      align-items: center;
      justify-content: center;
    `
  }}
`

const Divider = styled.div`
  ${({
    theme: {
      colors: {
        grey: { grey_70 },
      },
    },
  }) => {
    return css`
      background-color: ${grey_70};
      width: 2px;
      height: 16px;
      margin: 0 4px;
    `
  }}
`

const LanguageSwitcher: FunctionComponent = () => {
  const { isSearchBarCollapsed } = useHeaderSearchBar()
  const currentLanguage = getLanguage()

  const trackingOnClick = ({
    extraData,
  }: {
    extraData: { language: Locale }
  }) => {
    const { language } = extraData
    return {
      event: 'language_switch_home',
      eventCategory: 'language_switch',
      eventLabel: 'home',
      eventAction: language,
      relatedArticle: 'home',
    }
  }

  const handleTrack = useTracking(trackingOnClick)

  const onLanguageClick = useCallback(
    (language: Locale) => {
      // Track and set the cookie only if the user is switching the language
      if (currentLanguage !== language) {
        handleTrack({ language })
        setCookieValue({ name: 'bllang', value: language, expireDate: 'MAX' })
      }
    },
    [currentLanguage, handleTrack]
  )

  return (
    <Container isLanguageSwitcherVisible={!isSearchBarCollapsed}>
      <StyledClickable
        key="de"
        onClick={() => onLanguageClick('de')}
        href="/"
        ariaLabel="Ändern Sie die Sprache in Deutsch"
        selected={currentLanguage === 'de'}>
        DE
      </StyledClickable>
      <Divider />
      <StyledClickable
        key="fr"
        onClick={() => onLanguageClick('fr')}
        href="/fr/"
        ariaLabel="Changer la langue en français"
        selected={currentLanguage === 'fr'}>
        FR
      </StyledClickable>
    </Container>
  )
}

export default LanguageSwitcher
